import React, { useEffect, useState } from 'react';
import { customersAPI } from '../services/api';

interface CustomersModalProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const initialForm = {
  customer_name: '',
  email: '',
};

const CustomersModal: React.FC<CustomersModalProps> = ({ uuid, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isEditMode = !!uuid;

  useEffect(() => {
    if (isOpen && uuid) {
      setLoading(true);
      customersAPI.getById(uuid)
        .then(res => {
          const customer = res.data.customer || res.data;
          setForm({
            customer_name: customer.customer_name || '',
            email: customer.email || '',
          });
        })
        .catch(() => setError('Failed to load customer'))
        .finally(() => setLoading(false));
    } else {
      setForm(initialForm);
      setError('');
    }
  }, [isOpen, uuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    setLoading(true);
    setError('');
    const apiCall = isEditMode 
      ? customersAPI.update(uuid, form)
      : customersAPI.create(form);
    
    apiCall
      .then(() => onSave())
      .catch((err) => {
        const errorMsg = err.response?.data?.error || err.message || `Failed to ${isEditMode ? 'update' : 'create'} customer`;
        setError(errorMsg);
      })
      .finally(() => setLoading(false));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md relative">
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={onClose}>&times;</button>
        <h2 className="text-2xl font-bold mb-4">{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h2>
        {loading ? (
          <div className="text-center py-8 text-primary-600">Loading...</div>
        ) : (
          <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            <div>
              <label className="block text-sm font-semibold mb-1">Name</label>
              <input type="text" name="customer_name" value={form.customer_name} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="px-4 py-2 rounded-lg bg-gray-200" onClick={onClose}>Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold">Save</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CustomersModal;
